/**
 * TenantSidebar Component
 * Workspace-scoped navigation sidebar for the tenant admin application.
 *
 * Ported from legacy Layout.tsx (frontend-tenant) -- adapted for verisa_v1.
 *
 * Features:
 * - Background: #FAFAFA
 * - Desktop (>= 960px): Full sidebar, icons + text labels (260px)
 * - Tablet (600-959px): Collapsed icons only (60px) with tooltips
 * - Mobile (< 600px): Hidden, shown as temporary drawer on hamburger click
 * - Navigation: Agents, Sources, Usage, collapsible Workspace Settings
 * - Active state highlighting based on current route
 * - For a caller the server resolves unrestricted (staff), the "hidden for this
 *   tenant" badge under every entry the tenant hides or tiers - in brackets in
 *   the tablet rail's tooltips and flyouts, where a badge has no room
 *   (SPEC-TENANT-SURFACES RULE-012, TASK-TSURF-W7-STAFF-BADGES)
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  Collapse,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { usePermissionContext } from '../../store/PermissionContext';
import type { SurfaceKey } from '../../surfaces/keys';
import { usePagePresence } from '../../surfaces';
import { TenantMarkedLabel, useTenantMarkedText } from '../shared/TenantHiddenBadge';
import {
  ResizeHandle,
  useResizableWidth,
} from '../shared/ResizeHandle';
import { colors } from '../../theme/colors';
import { useFeature } from '../../feature-gate';
import CollapsedSectionPopover from '../shared/CollapsedSectionPopover';
import { useAgentNoun } from '../../utils/agentNoun';
import { useAgentsMenuTarget } from '../../apps/tenant/useAgentsMenuTarget';

// ---------------------------------------------------------------------------
// Release-toggle key mapping
// ---------------------------------------------------------------------------

/**
 * Maps a tenant sidebar path to its canonical release-toggle key, for the items
 * that the legacy `featureVisibility.ts` gated. Paths NOT present here were
 * always visible and stay always visible (no fail-closed regression).
 */
const TENANT_NAV_FEATURE_KEYS: Record<string, string> = {
  'sources/files': 'tenant.sources.files',
  'sources/website': 'tenant.sources.website',
  'settings/permissions': 'tenant.settings.permissions',
  // "Metadata" tab (Schemas page) — hidden while workspace document upload is
  // feature-flagged off (per-agent/workspace metadata schemas not relevant yet).
  'settings/schemas': 'tenant.settings.schemas',
  // "Extensions"/Plugins tab — hidden while the tenant plugin-management surface
  // is unfinished (fail-closed: OFF => nav item omitted).
  'settings/plugins': 'tenant.settings.plugins',
  // "AI answer records" (SPEC-FRONTEND-UI-058) - released per deployment once
  // the tenant control API is live there (TASK-AIMARK-TENANT-PROVENANCE-SETTINGS-UI).
  'settings/answer-provenance': 'tenant.settings.answer-provenance',
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Registry surface keys of the two collapsible nav GROUPS (kind nav_group in
 * spec-tenant-surfaces.registry.yaml, SPEC-TENANT-SURFACES-RULE-016). Each
 * item carries its own key inline below; the groups have no array entry of
 * their own, so their keys are declared here. A group renders only when its
 * own key AND at least one child are usable (RULE-005(c)).
 */
export const TENANT_NAV_GROUP_SURFACES = {
  sources: 'tenant.sources',
  settings: 'tenant.settings',
} as const satisfies Record<string, SurfaceKey>;

/** Sidebar width when expanded (desktop). Also the default and the MINIMUM. */
export const TENANT_SIDEBAR_WIDTH = 260;
/**
 * Narrowest the user may drag the expanded sidebar. Deliberately equal to the
 * shipped default (STORY-TA-RESIZABLE-NAV AC-003): resizing can only widen the
 * nav, so no existing layout, screenshot or Hungarian label review regresses.
 */
export const TENANT_SIDEBAR_MIN_WIDTH = TENANT_SIDEBAR_WIDTH;
/** Widest the user may drag the expanded sidebar. */
export const TENANT_SIDEBAR_MAX_WIDTH = 480;
/** localStorage key holding this sidebar's user-chosen width. */
export const TENANT_SIDEBAR_WIDTH_STORAGE_KEY = 'verisa.manage.sidebar.width';
/** Sidebar width when collapsed (tablet) */
export const TENANT_SIDEBAR_COLLAPSED_WIDTH = 60;
/** Pointer/touch hit width of the resize handle (WCAG 2.5.8 floor, RULE-046). */
const RESIZE_HANDLE_HIT_AREA_PX = 24;
/** Header height to offset the sidebar below the AppBar */
const HEADER_HEIGHT = 57;

// ---------------------------------------------------------------------------
// SVG Icons (ported from legacy Layout.tsx)
// ---------------------------------------------------------------------------

/** Agents icon SVG (matching legacy) */
const AgentsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    color="currentColor"
  >
    <path
      d="M11.5513 2.25C13.4654 2.24999 14.9971 2.24998 16.2271 2.37248C16.1143 2.66571 16.0303 2.86761 15.9766 2.98438C15.8159 3.05836 15.502 3.18172 15.003 3.36637L14.3059 3.6243C13.5211 3.91473 13 4.66312 13 5.5C13 6.33688 13.5211 7.08528 14.3059 7.3757L15.003 7.63363C15.502 7.81828 15.8198 7.94164 15.9805 8.01563C16.0545 8.17626 16.1817 8.49803 16.3664 8.99705L16.6243 9.69407C16.9147 10.4789 17.6631 11 18.5 11C19.3369 11 20.0853 10.4789 20.3757 9.69407L20.6336 8.99705C20.8183 8.49803 20.9416 8.17626 21.0156 8.01562C21.1323 7.96186 21.3341 7.88203 21.6272 7.77156C21.75 9.01601 21.75 10.6414 21.75 12.5572C21.75 14.7479 21.75 16.4686 21.5694 17.812C21.3843 19.1886 20.9973 20.2809 20.1391 21.1391C19.2809 21.9973 18.1886 22.3843 16.812 22.5694C15.4686 22.75 13.7479 22.75 11.5572 22.75C9.36646 22.75 7.53144 22.75 6.18802 22.5694C4.81137 22.3843 3.71911 21.9973 2.86091 21.1391C2.00272 20.2809 1.61568 19.1886 1.43059 17.812C1.24998 16.4686 1.24999 14.7479 1.25 12.5572V12.5572C1.24999 10.3665 1.24998 8.53144 1.43059 7.18802C1.61568 5.81137 2.00272 4.71911 2.86091 3.86091C3.71911 3.00272 4.81137 2.61568 6.18802 2.43059C7.53144 2.24998 9.36056 2.24999 11.5513 2.25H11.5513ZM13.9951 10C13.9951 11.3807 12.8742 12.5 11.4915 12.5C10.1089 12.5 8.98797 11.3807 8.98797 10C8.98797 8.61929 10.1089 7.5 11.4915 7.5C12.8742 7.5 13.9951 8.61929 13.9951 10ZM16.6512 16.8779C14.3633 12.874 8.63674 12.874 6.34882 16.8779C6.21617 17.11 6.21713 17.3952 6.35132 17.6264C6.48551 17.8577 6.73265 18 7 18H16C16.2674 18 16.5145 17.8577 16.6487 17.6264C16.7829 17.3952 16.7838 17.11 16.6512 16.8779Z"
      fill="currentColor"
      fillRule="evenodd"
    />
    <path
      d="M18.5 1.25C18.8138 1.25 19.0945 1.4454 19.2034 1.73972L19.4613 2.43675C19.8233 3.4151 19.9388 3.68091 20.1289 3.87106C20.3191 4.06121 20.5849 4.17667 21.5633 4.53869L22.2603 4.79661C22.5546 4.90552 22.75 5.18617 22.75 5.5C22.75 5.81383 22.5546 6.09448 22.2603 6.20339L21.5633 6.46131C20.5849 6.82333 20.3191 6.93879 20.1289 7.12894C19.9388 7.31909 19.8233 7.5849 19.4613 8.56325L19.2034 9.26028C19.0945 9.5546 18.8138 9.75 18.5 9.75C18.1862 9.75 17.9055 9.5546 17.7966 9.26028L17.5387 8.56325C17.1767 7.5849 17.0612 7.31909 16.8711 7.12894C16.6809 6.93879 16.4151 6.82333 15.4367 6.46131L14.7397 6.20339C14.4454 6.09448 14.25 5.81383 14.25 5.5C14.25 5.18617 14.4454 4.90552 14.7397 4.79661L15.4367 4.53869C16.4151 4.17667 16.6809 4.06121 16.8711 3.87106C17.0612 3.68091 17.1767 3.4151 17.5387 2.43675L17.7966 1.73972C17.9055 1.4454 18.1862 1.25 18.5 1.25Z"
      fill="currentColor"
    />
  </svg>
);

/** Sources icon SVG (matching legacy) */
const SourcesIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

/** Usage icon SVG (matching legacy) */
const UsageIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m10.286 4.503 2.282 6.428a1.5 1.5 0 0 0 .742.84l6.101 3.05M10.286 4.504a7.424 7.424 0 0 1 9.125 10.32m-9.125-10.32-.193-.544c-.277-.781-1.142-1.2-1.87-.802a9.5 9.5 0 1 0 12.27 13.878c.484-.674.175-1.585-.567-1.956l-.515-.257" />
  </svg>
);

/** AI answer records icon: a document with a check mark, in the same stroked style. */
const AnswerRecordsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <polyline points="9 15 11 17 15 13" />
  </svg>
);

/** Settings gear icon SVG (matching legacy) */
const SettingsIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    color="currentColor"
  >
    <path
      d="M9.38041 2.12028C9.17284 1.76673 8.71985 1.64537 8.36333 1.84779L4.89923 3.8146C4.72461 3.91374 4.59704 4.07876 4.54508 4.27272C4.49312 4.46667 4.5211 4.67337 4.62276 4.84653C5.76109 6.78545 4.30575 9.28324 2 9.28324C1.58579 9.28324 1.25 9.61902 1.25 10.0332L1.25 13.9669C1.25 14.3811 1.58579 14.7169 2 14.7169C4.30517 14.7169 5.75974 17.2141 4.62118 19.1535C4.51952 19.3266 4.49154 19.5333 4.5435 19.7273C4.59546 19.9212 4.72303 20.0863 4.89765 20.1854L8.36175 22.1522C8.71827 22.3546 9.17126 22.2333 9.37882 21.8797C10.5539 19.8782 13.4477 19.8782 14.6228 21.8796C14.8303 22.2332 15.2833 22.3545 15.6398 22.1521L19.1039 20.1853C19.2786 20.0862 19.4061 19.9211 19.4581 19.7272C19.51 19.5332 19.4821 19.3265 19.3804 19.1534C18.2416 17.2136 19.6954 14.7169 22 14.7169C22.4142 14.7169 22.75 14.3811 22.75 13.9669V10.0332C22.75 9.61902 22.4142 9.28324 22 9.28324C19.6948 9.28324 18.2403 6.78596 19.3788 4.84662C19.4805 4.67346 19.5085 4.46677 19.4565 4.27281C19.4045 4.07886 19.277 3.91384 19.1024 3.8147L15.6383 1.84789C15.2817 1.64546 14.8287 1.76683 14.6212 2.12038C13.4469 4.12059 10.5548 4.12055 9.38041 2.12028ZM12 15.5C13.933 15.5 15.5 13.933 15.5 12C15.5 10.067 13.933 8.5 12 8.5C10.067 8.5 8.5 10.067 8.5 12C8.5 13.933 10.067 15.5 12 15.5Z"
      fill="currentColor"
      fillRule="evenodd"
    />
  </svg>
);

/** Chevron icon for accordion expand/collapse */
const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NavMenuItem {
  text: string;
  icon: React.ReactNode;
  /** The section's own path: what the entry is highlighted on. */
  path: string;
  /**
   * Where a click goes, when that differs from `path` - an ABSOLUTE path. Only
   * the agents entry sets it, and only while it stands for the caller's one
   * agent (owner instruction 2026-10-05, `useAgentsMenuTarget`).
   */
  target?: string;
  /** Registry surface key (SPEC-TENANT-SURFACES-RULE-016/017); the entry renders only when it is usable. */
  surface: SurfaceKey;
  /** If true, only workspace admins see this item */
  adminOnly?: boolean;
}

interface SourcesSubItem {
  text: string;
  path: string;
  /** Registry surface key (SPEC-TENANT-SURFACES-RULE-016/017). */
  surface: SurfaceKey;
}

interface SettingsSubItem {
  text: string;
  path: string;
  /** Registry surface key (SPEC-TENANT-SURFACES-RULE-016/017). */
  surface: SurfaceKey;
  /** If true, only workspace admins see this item */
  adminOnly?: boolean;
  /** If set, only users with this permission see this item */
  permission?: string;
}

export interface TenantSidebarProps {
  /** Whether the mobile drawer is open */
  mobileOpen?: boolean;
  /** Handler to toggle mobile drawer */
  onMobileToggle?: () => void;
  /**
   * Called with the sidebar's current occupied width in CSS pixels whenever it
   * changes — on mount, on a breakpoint flip, and on every frame of a drag.
   * The layout uses it to keep the content area's offset in step with the
   * dragged sidebar; without it the content would only catch up on remount.
   */
  onWidthChange?: (width: number) => void;
}

// ---------------------------------------------------------------------------
// MenuItemComponent (ported from legacy Layout.tsx)
// ---------------------------------------------------------------------------

const MenuItemComponent: React.FC<{
  item: NavMenuItem;
  isSelected: boolean;
  collapsed: boolean;
  onNavigate: (path: string) => void;
}> = ({ item, isSelected, collapsed, onNavigate }) => {
  const markedText = useTenantMarkedText();
  const content = (
    <Box
      component="button"
      onClick={() => onNavigate(item.target ?? item.path)}
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        border: 'none',
        background: 'none',
        borderRadius: 1.5,
        px: collapsed ? 1.5 : 2.5,
        py: 1.5,
        fontWeight: isSelected ? 600 : 500,
        fontSize: '14px',
        transition: 'all 0.2s ease',
        gap: collapsed ? 0 : 1,
        color: isSelected ? colors.text.selected : colors.text.secondary,
        cursor: 'pointer',
        textDecoration: 'none',
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&:hover': {
          bgcolor: 'action.hover',
          color: colors.interactive.hover,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {/* Background highlight for selected item */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
          }}
        />
      )}

      {/* Icon */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          height: 20,
          width: 20,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          lineHeight: 1,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        {item.icon}
      </Box>

      {/* Text -- hidden in collapsed mode */}
      {!collapsed && (
        <TenantMarkedLabel surface={item.surface}>
          <Typography
            component="span"
            sx={{
              position: 'relative',
              zIndex: 10,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              fontSize: 'inherit',
              fontWeight: 'inherit',
              lineHeight: 1.2,
              color: 'inherit',
            }}
          >
            {item.text}
          </Typography>
        </TenantMarkedLabel>
      )}
    </Box>
  );

  // Wrap with tooltip only in collapsed mode
  if (collapsed) {
    return (
      <Tooltip title={markedText(item.text, item.surface)} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

// ---------------------------------------------------------------------------
// TenantSidebar
// ---------------------------------------------------------------------------

/**
 * TenantSidebar renders the workspace-scoped navigation drawer.
 *
 * Responsive behavior:
 * - Desktop (>= 960px): Permanent drawer, expanded (260px)
 * - Tablet (600-959px): Permanent drawer, collapsed (60px icons only)
 * - Mobile (< 600px): Temporary drawer, opens via hamburger menu
 */
const TenantSidebar: React.FC<TenantSidebarProps> = ({
  mobileOpen = false,
  onMobileToggle,
  onWidthChange,
}) => {
  const theme = useTheme();
  const rawNavigate = useNavigate();
  /**
   * Always navigate with absolute paths to avoid relative resolution issues; a
   * target that is already absolute (the agents entry's `target`) passes as is.
   * (The parameter is `to`, not `path`: tools/check-surface-registry.py counts
   * every `path: '...'` in this file as a nav entry, ternaries included.)
   */
  const navigate = (to: string) => rawNavigate(to.startsWith('/') ? to : '/' + to);
  const location = useLocation();
  const { _: lingui } = useLingui();
  // SPEC-FRONTEND-RULE-096 (D-368): the platform-chosen word for an agent.
  const { Noun, NounPlural } = useAgentNoun();
  // The owner, 2026-10-05 (08:49:17Z, 08:50:31Z): with exactly one agent and no
  // right to create another, the agents entry says the SINGULAR word and goes
  // straight into that agent - to the same page the landing chain would open.
  // Creation allowed, several agents, or any read unsettled or failed: the
  // plural word and the chooser, as before (useAgentsMenuTarget, landing.ts).
  const { single: singleAgentTarget } = useAgentsMenuTarget();
  const { hasPermission } = usePermissionContext();
  // The resolved surface set (SPEC-TENANT-SURFACES RULE-006 (1)): an entry the
  // caller cannot use is ABSENT, never disabled. Fails closed while loading
  // and on error, except the keys a 503 pinned (RULE-014, D-TSG-09).
  // D-TSG-23 point 3: a whole advanced page follows the holder's own switch.
  const { isShown } = usePagePresence();
  // The staff marker in the tablet rail, where a badge has no room (RULE-012).
  const markedText = useTenantMarkedText();

  // Release toggles for the gated tenant nav items. Hooks must run at top level,
  // so we resolve each gated path once and build a fail-closed visibility lookup.
  const sourcesFilesEnabled = useFeature(TENANT_NAV_FEATURE_KEYS['sources/files']);
  const sourcesWebsiteEnabled = useFeature(TENANT_NAV_FEATURE_KEYS['sources/website']);
  const settingsPermissionsEnabled = useFeature(TENANT_NAV_FEATURE_KEYS['settings/permissions']);
  const settingsSchemasEnabled = useFeature(TENANT_NAV_FEATURE_KEYS['settings/schemas']);
  const settingsPluginsEnabled = useFeature(TENANT_NAV_FEATURE_KEYS['settings/plugins']);
  const settingsAnswerProvenanceEnabled = useFeature(
    TENANT_NAV_FEATURE_KEYS['settings/answer-provenance'],
  );

  /**
   * Whether a tenant nav `path` is visible: an unkeyed path (not release-gated)
   * is always visible; a keyed path is visible only when its toggle is enabled.
   */
  const isNavPathVisible = (path: string): boolean => {
    switch (TENANT_NAV_FEATURE_KEYS[path]) {
      case undefined:
        return true;
      case 'tenant.sources.files':
        return sourcesFilesEnabled;
      case 'tenant.sources.website':
        return sourcesWebsiteEnabled;
      case 'tenant.settings.permissions':
        return settingsPermissionsEnabled;
      case 'tenant.settings.schemas':
        return settingsSchemasEnabled;
      case 'tenant.settings.plugins':
        return settingsPluginsEnabled;
      case 'tenant.settings.answer-provenance':
        return settingsAnswerProvenanceEnabled;
      default:
        return false;
    }
  };

  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isCollapsed = isTablet;

  // Drag-to-resize is DESKTOP-ONLY (STORY-TA-RESIZABLE-NAV AC-005): the tablet
  // rail owns its 60px collapsed width and the mobile temporary drawer keeps
  // its fixed width, so the hook is disabled in both and reports the default.
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const resize = useResizableWidth({
    storageKey: TENANT_SIDEBAR_WIDTH_STORAGE_KEY,
    defaultWidth: TENANT_SIDEBAR_WIDTH,
    minWidth: TENANT_SIDEBAR_MIN_WIDTH,
    maxWidth: TENANT_SIDEBAR_MAX_WIDTH,
    enabled: isDesktop,
  });

  const currentWidth = isCollapsed
    ? TENANT_SIDEBAR_COLLAPSED_WIDTH
    : resize.width;

  // Keep the layout's content offset in step with the live width.
  useEffect(() => {
    onWidthChange?.(currentWidth);
  }, [currentWidth, onWidthChange]);

  // Accordion states
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [workspaceSettingsExpanded, setWorkspaceSettingsExpanded] =
    useState(false);

  // Popover anchors for collapsed mode flyout menus
  const [sourcesPopoverAnchor, setSourcesPopoverAnchor] =
    useState<HTMLElement | null>(null);
  const [settingsPopoverAnchor, setSettingsPopoverAnchor] =
    useState<HTMLElement | null>(null);

  // ---- Navigation items ----

  const isWorkspaceAdmin = hasPermission('workspace.settings.manage');

  const menuItems: NavMenuItem[] = (
    [
      {
        text: singleAgentTarget === null ? NounPlural : Noun,
        icon: <AgentsIcon />,
        path: 'agents',
        target: singleAgentTarget ?? undefined,
        surface: 'tenant.agents',
      },
      {
        text: lingui(msg`Usage`),
        icon: <UsageIcon />,
        path: 'usage',
        surface: 'tenant.usage',
      },
      // A TOP-LEVEL entry, not a Workspace Settings child: the page is a legal
      // control no platform configuration may hide (configurable: false,
      // RULE-005(a)), and a hideable group would take it along (RULE-004,
      // RULE-006 (1)). Owner ruling, Tibor Igneczi 2026-09-25T21:20:38Z, "B:
      // top-level entry". The URL stays settings/answer-provenance.
      {
        text: lingui(msg`AI answer records`),
        icon: <AnswerRecordsIcon />,
        path: 'settings/answer-provenance',
        surface: 'tenant.settings.answer-provenance',
        adminOnly: true,
      },
    ] as NavMenuItem[]
  ).filter(
    (item) =>
      (!item.adminOnly || isWorkspaceAdmin) && isNavPathVisible(item.path) && isShown(item.surface),
  );

  // ---- Sources sub-items (collapsible accordion, like Settings) ----

  // An unusable group key empties the group, and an empty group does not
  // render (RULE-005(c)); the render sites below test `.length > 0`.
  const sourcesGroupUsable = isShown(TENANT_NAV_GROUP_SURFACES.sources);
  const sourcesSubItems: SourcesSubItem[] = [
    { text: lingui(msg`Files`), path: 'sources/files', surface: 'tenant.sources.files' },
    { text: lingui(msg`Website`), path: 'sources/website', surface: 'tenant.sources.website' },
  ].filter((item) => sourcesGroupUsable && isNavPathVisible(item.path) && isShown(item.surface));

  const isSourcesSubItemSelected = (subItem: { path: string }): boolean => {
    const segment = `/${subItem.path}`;
    return location.pathname === segment || location.pathname.startsWith(`${segment}/`);
  };

  const isAnySourcesSelected = sourcesSubItems.some((item) =>
    isSourcesSubItemSelected(item),
  );

  // ---- Workspace settings sub-items ----

  const allSettingsSubItems: SettingsSubItem[] = [
    { text: lingui(msg`General`), path: 'settings/general', surface: 'tenant.settings.general' },
    { text: lingui(msg`Members`), path: 'settings/members', surface: 'tenant.settings.members', permission: 'workspace.members.manage' },
    {
      text: lingui(msg`Permissions`),
      path: 'settings/permissions',
      surface: 'tenant.settings.permissions',
      adminOnly: true,
    },
    { text: lingui(msg`Roles`), path: 'settings/roles', surface: 'tenant.settings.roles', adminOnly: true },
    { text: lingui(msg`Plugins`), path: 'settings/plugins', surface: 'tenant.settings.plugins', adminOnly: true },
    { text: lingui(msg`Schemas`), path: 'settings/schemas', surface: 'tenant.settings.schemas' },
  ];

  const settingsGroupUsable = isShown(TENANT_NAV_GROUP_SURFACES.settings);
  const settingsSubItems = allSettingsSubItems.filter(
    (item) =>
      settingsGroupUsable &&
      (!item.adminOnly || isWorkspaceAdmin) &&
      (!item.permission || hasPermission(item.permission)) &&
      isNavPathVisible(item.path) &&
      isShown(item.surface),
  );

  // ---- Path matching helpers ----

  const isMenuItemSelected = (item: NavMenuItem): boolean => {
    const segment = `/${item.path}`;
    return location.pathname === segment || location.pathname.startsWith(`${segment}/`);
  };

  const isSettingsSubItemSelected = (subItem: SettingsSubItem): boolean => {
    const segment = `/${subItem.path}`;
    return location.pathname === segment || location.pathname.startsWith(`${segment}/`);
  };

  const isAnySettingsSelected = settingsSubItems.some((item) =>
    isSettingsSubItemSelected(item),
  );

  // ---- Drawer content renderer ----

  const createDrawerContent = (collapsed: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 1,
        pt: 0.5,
        pb: 8,
        height: '100%',
      }}
    >
      {/* Navigation Items */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {menuItems.map((item) => (
          <MenuItemComponent
            key={item.path}
            item={item}
            isSelected={isMenuItemSelected(item)}
            collapsed={collapsed}
            onNavigate={navigate}
          />
        ))}

        {/* Sources -- Accordion (expanded mode only) */}
        {!collapsed && sourcesSubItems.length > 0 && (
          <Box>
            <Box
              component="button"
              onClick={() => setSourcesExpanded(!sourcesExpanded)}
              aria-expanded={sourcesExpanded}
              data-state={sourcesExpanded ? 'open' : 'closed'}
              sx={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                borderRadius: 1.5,
                px: 2.5,
                py: 1.5,
                fontWeight: isAnySourcesSelected ? 600 : 500,
                fontSize: '14px',
                transition: 'all 0.075s ease-in-out',
                gap: 1,
                color: isAnySourcesSelected
                  ? colors.text.selected
                  : colors.text.secondary,
                cursor: 'pointer',
                border: 'none',
                bgcolor: 'transparent',
                outline: 'none',
                textAlign: 'left',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: colors.interactive.hover,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              {isAnySourcesSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                    opacity: 1,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              )}

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 10,
                  display: 'flex',
                  flex: 1,
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    height: 20,
                    width: 20,
                    flexShrink: 0,
                    '& svg': { width: '100%', height: '100%' },
                  }}
                >
                  <SourcesIcon />
                </Box>

                <TenantMarkedLabel surface={TENANT_NAV_GROUP_SURFACES.sources}>
                  <Typography
                    component="span"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      lineHeight: 1.2,
                      color: 'inherit',
                    }}
                  >
                    <Trans>Sources</Trans>
                  </Typography>
                </TenantMarkedLabel>
              </Box>

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 10,
                  height: 16,
                  width: 16,
                  flexShrink: 0,
                  transform: sourcesExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.075s ease',
                  '& svg': { width: '100%', height: '100%' },
                }}
              >
                <ChevronIcon />
              </Box>
            </Box>

            <Collapse in={sourcesExpanded} timeout={200} unmountOnExit>
              <Box sx={{ position: 'relative', ml: 4.5, mt: 0.5, mb: 0.5 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    bgcolor: colors.border.default,
                  }}
                />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {sourcesSubItems.map((subItem) => {
                    const isSubSelected = isSourcesSubItemSelected(subItem);

                    return (
                      <Box
                        key={subItem.path}
                        component="button"
                        onClick={() => navigate(subItem.path)}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                          fontWeight: isSubSelected ? 600 : 400,
                          fontSize: '13px',
                          transition: 'all 0.075s ease',
                          color: isSubSelected
                            ? colors.text.selected
                            : colors.text.secondary,
                          cursor: 'pointer',
                          border: 'none',
                          bgcolor: 'transparent',
                          textAlign: 'left',
                          width: '100%',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            color: colors.interactive.hover,
                          },
                        }}
                      >
                        <TenantMarkedLabel surface={subItem.surface}>
                          <Typography
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 'inherit',
                              fontWeight: isSubSelected ? 600 : 400,
                              color: 'inherit',
                            }}
                          >
                            {subItem.text}
                          </Typography>
                        </TenantMarkedLabel>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Sources -- Collapsed mode (icon with popover flyout) */}
        {collapsed && sourcesSubItems.length > 0 && (
          <>
            <Tooltip
              title={markedText(lingui(msg`Sources`), TENANT_NAV_GROUP_SURFACES.sources)}
              placement="right"
              arrow
              disableHoverListener={Boolean(sourcesPopoverAnchor)}
            >
              <Box
                component="button"
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setSourcesPopoverAnchor(e.currentTarget)
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  py: 1.5,
                  border: 'none',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  color: isAnySourcesSelected ? colors.text.selected : colors.text.secondary,
                  '&:hover': { bgcolor: 'action.hover' },
                  borderRadius: 1.5,
                }}
              >
                <Box sx={{ height: 20, width: 20, '& svg': { width: '100%', height: '100%' } }}>
                  <SourcesIcon />
                </Box>
              </Box>
            </Tooltip>
            <CollapsedSectionPopover
              title={lingui(msg`Sources`)}
              anchorEl={sourcesPopoverAnchor}
              open={Boolean(sourcesPopoverAnchor)}
              onClose={() => setSourcesPopoverAnchor(null)}
              items={sourcesSubItems.map((item) => ({
                text: item.text,
                path: item.path,
                isSelected: isSourcesSubItemSelected(item),
                surface: item.surface,
              }))}
              onNavigate={navigate}
            />
          </>
        )}

        {/* Workspace Settings -- Accordion (expanded mode only) */}
        {!collapsed && settingsSubItems.length > 0 && (
          <Box>
            {/* Accordion trigger */}
            <Box
              component="button"
              onClick={() =>
                setWorkspaceSettingsExpanded(!workspaceSettingsExpanded)
              }
              aria-expanded={workspaceSettingsExpanded}
              data-state={workspaceSettingsExpanded ? 'open' : 'closed'}
              sx={{
                position: 'relative',
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                borderRadius: 1.5,
                px: 2.5,
                py: 1.5,
                fontWeight: isAnySettingsSelected ? 600 : 500,
                fontSize: '14px',
                transition: 'all 0.075s ease-in-out',
                gap: 1,
                color: isAnySettingsSelected
                  ? colors.text.selected
                  : colors.text.secondary,
                cursor: 'pointer',
                border: 'none',
                bgcolor: 'transparent',
                outline: 'none',
                textAlign: 'left',
                '&:hover': {
                  bgcolor: 'action.hover',
                  color: colors.interactive.hover,
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              {/* Background highlight when a sub-item is selected */}
              {isAnySettingsSelected && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                    opacity: 1,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              )}

              {/* Icon + text container */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 10,
                  display: 'flex',
                  flex: 1,
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {/* Settings icon */}
                <Box
                  sx={{
                    height: 20,
                    width: 20,
                    flexShrink: 0,
                    '& svg': { width: '100%', height: '100%' },
                  }}
                >
                  <SettingsIcon />
                </Box>

                {/* Text */}
                <TenantMarkedLabel surface={TENANT_NAV_GROUP_SURFACES.settings}>
                  <Typography
                    component="span"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'left',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                      lineHeight: 1.2,
                      color: 'inherit',
                    }}
                  >
                    <Trans>Workspace Settings</Trans>
                  </Typography>
                </TenantMarkedLabel>
              </Box>

              {/* Expand/collapse chevron */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 10,
                  height: 16,
                  width: 16,
                  flexShrink: 0,
                  transform: workspaceSettingsExpanded
                    ? 'rotate(180deg)'
                    : 'rotate(0deg)',
                  transition: 'transform 0.075s ease',
                  '& svg': { width: '100%', height: '100%' },
                }}
              >
                <ChevronIcon />
              </Box>
            </Box>

            {/* Sub-items with collapse animation */}
            <Collapse
              in={workspaceSettingsExpanded}
              timeout={200}
              unmountOnExit
            >
              <Box sx={{ position: 'relative', ml: 4.5, mt: 0.5, mb: 0.5 }}>
                {/* Vertical connecting line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    bgcolor: colors.border.default,
                  }}
                />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  {settingsSubItems.map((subItem) => {
                    const isSubSelected = isSettingsSubItemSelected(subItem);

                    return (
                      <Box
                        key={subItem.path}
                        component="button"
                        onClick={() => navigate(subItem.path)}
                        sx={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center',
                          borderRadius: 1,
                          px: 2,
                          py: 1,
                          fontWeight: isSubSelected ? 600 : 400,
                          fontSize: '13px',
                          transition: 'all 0.075s ease',
                          color: isSubSelected
                            ? colors.text.selected
                            : colors.text.secondary,
                          cursor: 'pointer',
                          border: 'none',
                          bgcolor: 'transparent',
                          textAlign: 'left',
                          width: '100%',
                          '&:hover': {
                            bgcolor: 'action.hover',
                            color: colors.interactive.hover,
                          },
                        }}
                      >
                        <TenantMarkedLabel surface={subItem.surface}>
                          <Typography
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 'inherit',
                              fontWeight: isSubSelected ? 600 : 400,
                              color: 'inherit',
                            }}
                          >
                            {subItem.text}
                          </Typography>
                        </TenantMarkedLabel>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Workspace Settings -- Collapsed mode (icon with popover flyout) */}
        {collapsed && settingsSubItems.length > 0 && (
          <>
            <Tooltip
              title={markedText(lingui(msg`Workspace Settings`), TENANT_NAV_GROUP_SURFACES.settings)}
              placement="right"
              arrow
              disableHoverListener={Boolean(settingsPopoverAnchor)}
            >
              <Box
                component="button"
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setSettingsPopoverAnchor(e.currentTarget)
                }
                sx={{
                  position: 'relative',
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'none',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1.5,
                  color: isAnySettingsSelected
                    ? colors.text.selected
                    : colors.text.secondary,
                  cursor: 'pointer',
                  textDecoration: 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: colors.interactive.hover,
                  },
                }}
              >
                {isAnySettingsSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper',
                      boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 10,
                    height: 20,
                    width: 20,
                    '& svg': { width: '100%', height: '100%' },
                  }}
                >
                  <SettingsIcon />
                </Box>
              </Box>
            </Tooltip>
            <CollapsedSectionPopover
              title={lingui(msg`Workspace Settings`)}
              anchorEl={settingsPopoverAnchor}
              open={Boolean(settingsPopoverAnchor)}
              onClose={() => setSettingsPopoverAnchor(null)}
              items={settingsSubItems.map((item) => ({
                text: item.text,
                path: item.path,
                isSelected: isSettingsSubItemSelected(item),
                surface: item.surface,
              }))}
              onNavigate={navigate}
            />
          </>
        )}
      </Box>

    </Box>
  );

  return (
    <>
      {/* Mobile Drawer -- Temporary */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: TENANT_SIDEBAR_WIDTH,
            top: `${HEADER_HEIGHT}px`,
            height: `calc(100% - ${HEADER_HEIGHT}px)`,
            bgcolor: '#FAFAFA',
          },
        }}
      >
        {createDrawerContent(false)}
      </Drawer>

      {/* Desktop / Tablet Drawer -- Permanent.
          IN FLOW, NOT VIEWPORT-FIXED (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29).
          The paper used to be `position: fixed; top: HEADER_HEIGHT;
          height: calc(100% - HEADER_HEIGHT)`, so it ran to the bottom edge of
          the VIEWPORT and would now sit over the shell's full-width bottom bar.
          `position: relative` with `height: 100%` makes it take its height from
          the row it lives in (apps/tenant/App.tsx), so the navigation ENDS where
          the row ends - above the bar - which is the owner's sentence. The
          `height: 100%` on the docked ROOT is what gives the paper a definite
          height to resolve against; the row's own `marginTop` supplies the
          header offset the old `top` was providing, once instead of per child.
          NOT DONE: keeping it fixed and subtracting a footer height. The bar
          grows when the Hungarian labels wrap and again when the imprint opens,
          so any constant would be wrong exactly when it mattered. */}
      <Drawer
        variant="permanent"
        sx={{ display: { xs: 'none', sm: 'block' }, height: '100%' }}
        // THE PAPER'S OWN sx, not a `& .MuiDrawer-paper` descendant selector on
        // the root. `position: relative` has to beat MUI's own
        // `position: fixed` on the paper (Drawer.js). A descendant selector on
        // the root wins that on SPECIFICITY, which a real browser applies and
        // jsdom does not: jsdom resolves the cascade in insertion order alone,
        // and the paper's base class is inserted when the paper renders, after
        // the root's - so `fixed` won there and the flow layout was provable
        // only in a browser we do not have. Through the slot the declaration
        // lands on the paper's OWN sx class, which is inserted after that base
        // class, so it wins under both resolution models.
        // `slotProps.paper` is the v6 API; `PaperProps` is deprecated.
        slotProps={{
          paper: {
            sx: {
              boxSizing: 'border-box',
              width: currentWidth,
              position: 'relative',
              height: '100%',
              bgcolor: '#FAFAFA',
              // The collapse/expand animation must not run during a drag, or
              // the paper lags a frame behind the pointer and the drag feels
              // broken.
              transition: resize.isDragging ? 'none' : 'width 0.2s ease-in-out',
              overflowX: 'hidden',
              overflowY: 'auto',
            },
          },
        }}
        open
      >
        {createDrawerContent(isCollapsed)}
      </Drawer>

      {/*
        Resize handle. Still a positioned SIBLING of the drawer rather than a
        child of the paper: the paper is a scrolling container, so a handle
        inside it would scroll out of reach on a long nav.

        IT FOLLOWS THE ROW, NOT THE VIEWPORT (RULE-020, D-BR-29). It used to be
        `position: fixed; top: HEADER_HEIGHT; bottom: 0`, which ran it to the
        viewport's bottom edge - over the shell's new bottom bar, and past the
        end of the drawer it resizes. `position: absolute` resolves against the
        nearest positioned ancestor, which is the `component="nav"` box in
        apps/tenant/App.tsx: that box is the flex item of the row, so `top: 0;
        bottom: 0` is now the ROW's height and the handle spans exactly the rows
        the drawer spans. `left` is unchanged in value and in meaning - the nav
        box's left edge is the drawer's left edge.
      */}
      {resize.enabled && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${currentWidth - RESIZE_HANDLE_HIT_AREA_PX / 2}px`,
            display: 'flex',
            zIndex: (muiTheme) => muiTheme.zIndex.drawer + 3,
            transition: resize.isDragging ? 'none' : 'left 0.2s ease-in-out',
          }}
        >
          <ResizeHandle
            resize={resize}
            position="static"
            hitAreaPx={RESIZE_HANDLE_HIT_AREA_PX}
            ariaLabel={lingui(msg`Resize the workspace navigation`)}
            data-testid="tenant-sidebar-resize-handle"
          />
        </Box>
      )}
    </>
  );
};

export default TenantSidebar;
